import React, { useState, useEffect } from 'react';
import { X, Shield, Lock, FileWarning, FilePen, Loader2, Check, AlertTriangle } from 'lucide-react';
import api from '../../services/api';

/**
 * Protection category icons
 */
const PROTECTION_ICONS = {
  work_product: Shield,
  privilege: Lock,
  confidential: FileWarning,
  draft: FilePen
};

const PROTECTION_COLORS = {
  work_product: 'text-red-400 bg-red-500/20 border-red-500/30',
  privilege: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
  confidential: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
  draft: 'text-slate-400 bg-slate-500/20 border-slate-500/30'
};

/**
 * Modal for managing protection categories on a document
 */
const ManageProtectionsModal = ({ isOpen, onClose, document, onUpdate }) => {
  const [categories, setCategories] = useState([]);
  const [currentProtections, setCurrentProtections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // Track which category is being saved
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && document) {
      fetchData();
    }
  }, [isOpen, document]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch categories and current protections in parallel
      const [categoriesRes, protectionsRes] = await Promise.all([
        api.get('/api/documents/protection-categories'),
        api.get(`/api/documents/${document.id}/protections`)
      ]);
      setCategories(categoriesRes.data);
      setCurrentProtections(protectionsRes.data);
    } catch (err) {
      console.error('Error fetching protection data:', err);
      setError('Failed to load protection categories');
    } finally {
      setLoading(false);
    }
  };

  const isProtected = (categoryCode) => {
    return currentProtections.some(p => p.category_code === categoryCode);
  };

  const toggleProtection = async (category) => {
    setSaving(category.code);
    setError(null);

    try {
      if (isProtected(category.code)) {
        // Remove protection
        await api.delete(`/api/documents/${document.id}/protections/${category.code}`);
        setCurrentProtections(prev => prev.filter(p => p.category_code !== category.code));
      } else {
        // Add protection
        const response = await api.post(`/api/documents/${document.id}/protections`, {
          category_code: category.code
        });
        setCurrentProtections(prev => [...prev, response.data]);
      }

      // Notify parent of update
      if (onUpdate) {
        onUpdate(document.id, currentProtections);
      }
    } catch (err) {
      console.error('Error toggling protection:', err);
      setError(err.response?.data?.detail || 'Failed to update protection');
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4 border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-white">Manage Protections</h2>
            <p className="text-sm text-slate-400 truncate max-w-[300px]" title={document?.name}>
              {document?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700 rounded transition-colors"
          >
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-500" size={24} />
              <span className="ml-2 text-slate-400">Loading...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400">
              <AlertTriangle size={18} />
              <span>{error}</span>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-400 mb-4">
                Protected documents cannot be shared with clients. Select the applicable protections:
              </p>

              {categories.map(category => {
                const Icon = PROTECTION_ICONS[category.code] || Shield;
                const colorClasses = PROTECTION_COLORS[category.code] || 'text-slate-400 bg-slate-500/20 border-slate-500/30';
                const isActive = isProtected(category.code);
                const isSaving = saving === category.code;

                return (
                  <button
                    key={category.code}
                    onClick={() => toggleProtection(category)}
                    disabled={isSaving}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-lg border transition-all
                      ${isActive
                        ? colorClasses + ' ring-2 ring-offset-2 ring-offset-slate-800 ring-white/20'
                        : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                      }
                      ${isSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                    `}
                  >
                    <div className={`p-2 rounded ${isActive ? '' : 'bg-slate-600'}`}>
                      {isSaving ? (
                        <Loader2 className="animate-spin" size={20} />
                      ) : (
                        <Icon size={20} className={isActive ? '' : 'text-slate-400'} />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className={`font-medium ${isActive ? '' : 'text-white'}`}>
                        {category.name}
                      </div>
                      {category.description && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          {category.description}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <div className="p-1 bg-white/10 rounded-full">
                        <Check size={16} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageProtectionsModal;
